/**
 * Distribution screen (task 005-08) — one ops surface over the distribution
 * pipeline: the **Attempts** log (filterable list + drill-down) and the
 * **Dead letters** queue (list + Retry).
 *
 * The active tab rides the URL query (`?tab=dlq`) so a reload or a shared link
 * reopens the same tab. Read-only except the DLQ's Retry.
 */

import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/page-header';
import { AttemptsTab } from './attempts-tab';
import { DlqTab } from './dlq-tab';

type DistributionTab = 'attempts' | 'dlq';

const TAB_QUERY_KEY = 'tab';

const TABS: ReadonlyArray<readonly [DistributionTab, string]> = [
  ['attempts', 'Attempts'],
  ['dlq', 'Dead letters'],
];

function tabFromQuery(value: string | null): DistributionTab {
  return value === 'dlq' ? 'dlq' : 'attempts';
}

export function Distribution() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromQuery(searchParams.get(TAB_QUERY_KEY));

  const selectTab = (next: DistributionTab) => {
    // Attempts is the canonical /distribution view — no query.
    setSearchParams(next === 'dlq' ? { tab: 'dlq' } : {});
  };

  return (
    <div>
      <PageHeader
        kicker="Operations"
        title="Distribution"
        desc="Every distribution attempt and the dead-letter queue. Read-only except Retry."
        actions={
          <div className="flex gap-1">
            {TABS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => selectTab(value)}
                aria-current={tab === value ? 'page' : undefined}
                className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition ${
                  tab === value
                    ? 'bg-accent text-white'
                    : 'bg-paper text-faint hover:text-ink hairline'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />

      {tab === 'dlq' ? <DlqTab /> : <AttemptsTab />}
    </div>
  );
}
